'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { ComponentProps, createContext, useContext } from 'react';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { ModalService } from '@/shared/ui/modal/ModalService';
import './contact.scss';

const DeliveryAreasMap = dynamic(
    () => import('@/features/select-venue/ui/delivery-area-map').then((mod) => mod.DeliveryAreasMap),
    {
        ssr: false,
        loading: () => <div style={{ height: '100%', minHeight: '12rem' }} />,
    },
);

type ContactDeliveryAreaBoxContextValue = {
    handleShowMap?: () => Promise<void>;
};

const ContactDeliveryAreaBoxContext = createContext<ContactDeliveryAreaBoxContextValue>(
    {} as ContactDeliveryAreaBoxContextValue,
);

type ContactDeliveryAreaBoxWrapperProps = ComponentProps<typeof Layout>;

export const ContactDeliveryAreaBoxWrapper = ({ children, ...props }: ContactDeliveryAreaBoxWrapperProps) => {
    const handleShowMap = async () => {
        await ModalService.showPrefab('DeliveryZonesModal', {});
    };

    const contextValue: ContactDeliveryAreaBoxContextValue = {
        handleShowMap,
    };

    return (
        <ContactDeliveryAreaBoxContext.Provider value={contextValue}>
            <Layout className="card contact__box" {...props}>
                {children}
            </Layout>
        </ContactDeliveryAreaBoxContext.Provider>
    );
};

type ContactDeliveryAreaBoxTitleProps = ComponentProps<typeof Layout>;

export const ContactDeliveryAreaBoxTitle = ({ media, ...props }: ContactDeliveryAreaBoxTitleProps) => {
    const t = useTranslations();
    return (
        <Layout
            element="span"
            className="contact__box-title"
            media={media}
            data-testid="contact-delivery-area-title"
            {...props}
        >
            {t('CONTACT.DELIVERY_AREA_BOX.TITLE')}
        </Layout>
    );
};

type ContactDeliveryAreaBoxMapWrapperProps = ComponentProps<typeof Layout>;

export const ContactDeliveryAreaBoxMapWrapper = ({
    media,
    ...props
}: ContactDeliveryAreaBoxMapWrapperProps) => {
    return <Layout className="contact__delivery-area-box__map-wrapper" media={media} {...props} />;
};

type ContactDeliveryAreaBoxMapOverlayProps = ComponentProps<typeof Layout>;

export const ContactDeliveryAreaBoxMapOverlay = ({
    media,
    ...props
}: ContactDeliveryAreaBoxMapOverlayProps) => {
    return <Layout className="contact__delivery-area-box__map-overlay" media={media} {...props} />;
};

type ContactDeliveryAreaBoxButtonProps = ComponentProps<typeof Layout>;

export const ContactDeliveryAreaBoxButton = ({
    onClick,
    media,
    children,
    ...props
}: ContactDeliveryAreaBoxButtonProps) => {
    const t = useTranslations();
    const context = useContext(ContactDeliveryAreaBoxContext);

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
            onClick(e);
        }
        if (context.handleShowMap) {
            await context.handleShowMap();
        }
    };

    return (
        <Layout media={media} {...props}>
            <Button size="small" onClick={handleClick} data-testid="contact-delivery-area-button">
                {children ?? t('CONTACT.DELIVERY_AREA_BOX.BUTTON')}
            </Button>
        </Layout>
    );
};

type ContactDeliveryAreaBoxMapProps = ComponentProps<typeof Layout> & { darkMode?: boolean };

export const ContactDeliveryAreaBoxMap = ({ media, darkMode, ...props }: ContactDeliveryAreaBoxMapProps) => {
    return (
        <Layout media={media} {...props}>
            <DeliveryAreasMap
                className="contact__delivery-area-box__map"
                showAllZones
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                zoomControl={false}
                attributionControl={false}
                darkMode={darkMode}
            />
        </Layout>
    );
};

// Legacy export for backward compatibility
export const ContactDeliveryAreaBox = ContactDeliveryAreaBoxWrapper;

// Compound component structure
export const ContactDeliveryAreaBoxCompound: typeof ContactDeliveryAreaBoxWrapper & {
    Title: typeof ContactDeliveryAreaBoxTitle;
    MapWrapper: typeof ContactDeliveryAreaBoxMapWrapper;
    MapOverlay: typeof ContactDeliveryAreaBoxMapOverlay;
    Button: typeof ContactDeliveryAreaBoxButton;
    Map: typeof ContactDeliveryAreaBoxMap;
} = Object.assign(ContactDeliveryAreaBoxWrapper, {
    Title: ContactDeliveryAreaBoxTitle,
    MapWrapper: ContactDeliveryAreaBoxMapWrapper,
    MapOverlay: ContactDeliveryAreaBoxMapOverlay,
    Button: ContactDeliveryAreaBoxButton,
    Map: ContactDeliveryAreaBoxMap,
});
